/**
 * Tour Card
 *
 * Custom Onborda card for the shared discoverability tour.
 *
 * Primary responsibilities:
 * - Render step copy and navigation controls
 * - Persist tour completion when the user exits
 *
 * Dependencies:
 * - Onborda
 * - Discoverability context
 */

'use client'

import React from 'react'
import type { CardComponentProps } from 'onborda'
import { useOnborda } from 'onborda'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  Cross2Icon,
  InfoCircledIcon
} from '@radix-ui/react-icons'

import { Button } from '@/components/ui/button'
import { useDiscoverability } from '@/lib/discoverability/context'

export const TourCard: React.FC<CardComponentProps> = ({
  step,
  currentStep,
  totalSteps,
  nextStep,
  prevStep,
  arrow
}) => {
  const { closeOnborda } = useOnborda()
  const { activeTourId, completeTour } = useDiscoverability()

  function handleClose() {
    closeOnborda()
  }

  function handleFinish() {
    completeTour(activeTourId === 'main' ? 'main' : undefined)
  }

  return (
    <div className="bg-background min-w-[300px] max-w-[400px] rounded-lg border p-4 shadow-xl">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">
          {currentStep + 1} of {totalSteps}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="text-muted-foreground size-7"
          aria-label="Close tour"
        >
          <Cross2Icon className="size-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="mb-4 space-y-2">
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <span className="bg-muted flex size-7 items-center justify-center rounded-md border">
            <InfoCircledIcon className="text-primary size-4" aria-hidden="true" />
          </span>
          {step.title}
        </h3>
        <div className="text-muted-foreground text-sm leading-5">{step.content}</div>
      </div>

      <div className="flex items-center justify-between">
        {currentStep !== 0 ? (
          <Button variant="outline" size="sm" onClick={prevStep}>
            <ArrowLeftIcon className="size-4" aria-hidden="true" />
            Previous
          </Button>
        ) : (
          <div />
        )}
        {currentStep + 1 !== totalSteps ? (
          <Button variant="default" size="sm" onClick={nextStep}>
            Next
            <ArrowRightIcon className="size-4" aria-hidden="true" />
          </Button>
        ) : (
          <Button variant="default" size="sm" onClick={handleFinish}>
            <CheckIcon className="size-4" aria-hidden="true" />
            Finish
          </Button>
        )}
      </div>
      <span className="text-primary">{arrow}</span>
    </div>
  )
}
